// Shell presentacional de troca de senha autenticada (F3-D).
//
// Extraído da ChangePasswordPage do web-comite e levado ao admin/recurso. É PURO
// de infra: sem crypto, sem fetch, sem i18n. Recebe labels (i18n local de cada
// app), minLength e callbacks. A derivação Argon2id + POST /change-password vive
// no auth-client de cada app, injetada via onSubmit.
//
// Montado 100% com primitivos (Card/Field/Input/Button/Alert) → tematizado e
// dark-safe via tokens reais, sem depender de classes globais por-SPA nem dos
// aliases --color-* (a armadilha de runtime corrigida na F3-B).

import { useState, type FormEvent } from 'react';
import { Card } from './Card';
import { Field, Input } from './Input';
import { Button } from './Button';
import { Alert } from './Alert';

/** Mínimo de caracteres da nova senha (fonte única — apps interpolam labels com isto). */
export const CHANGE_PASSWORD_MIN_LENGTH = 12;

export interface ChangePasswordLabels {
  title: string;
  intro: string;
  currentLabel: string;
  newLabel: string;
  confirmLabel: string;
  totpLabel: string;
  submit: string;
  submitting: string;
  cancel: string;
  doneTitle: string;
  doneMessage: string;
  doneButton: string;
  errTooShort: string;
  errMismatch: string;
  errSameAsCurrent: string;
  errInvalidTotp: string;
  errInvalidCredentials: string;
  errNotAuthenticated: string;
  /** OPCIONAL de propósito — ausente cai em `errGeneric` (ver o handleSubmit).
   *
   *  ⚠️ REGRA DE CONTRATO deste pacote: prop nova em componente COMPARTILHADO nasce
   *  OPCIONAL. `ui-primitives` é API pública interna — o que muda aqui atravessa
   *  comitê, admin, recurso e intake de uma vez. Tornar obrigatória é permitido, mas
   *  só tocando TODOS os consumidores no MESMO ato; caso contrário quem não tem o
   *  rótulo para de compilar.
   *
   *  Aconteceu em 29/07: este campo nasceu obrigatório para uma mensagem que só o
   *  i18n do COMITÊ tem, e derrubou o tsc do web-recurso (App.tsx:253) e do
   *  web-intake — duas SPAs que nada tinham a ver com a correção. Agravante: o
   *  rótulo está represado pela migration 019, então "adicionar em cada SPA" nem
   *  era possível sem arrastar trabalho de outra frente. */
  errRecarregarPagina?: string;
  errGeneric: string;
}

/** Resultado do POST de troca de senha, normalizado pelo app a partir do auth-client. */
export type ChangePasswordSubmitResult =
  | { ok: true }
  | {
      ok: false;
      error:
        | 'invalid_credentials'
        | 'same_password'
        | 'not_authenticated'
        /** Tela desatualizada (bundle antigo em aba aberta) OU leitura desabilitada:
         *  o servidor recusou porque a privada re-embrulhada nao veio junto. Precisa
         *  de mensagem PROPRIA — o generico nao diz "recarregue", que e a acao. */
        | 'recarregar_pagina'
        | 'other';
    };

export interface ChangePasswordFormProps {
  labels: ChangePasswordLabels;
  /** Mínimo de caracteres da nova senha (default {@link CHANGE_PASSWORD_MIN_LENGTH}). */
  minLength?: number;
  /** Deriva os hashes e POSTa no backend. O shell cuida de estado/validação. */
  onSubmit: (
    currentPassword: string,
    newPassword: string,
    totpCode: string,
  ) => Promise<ChangePasswordSubmitResult>;
  /** Cancela e volta (navegação a cargo do app). */
  onCancel: () => void;
  /** Concluído — volta ao painel após o sucesso (navegação a cargo do app). */
  onDone: () => void;
}

export function ChangePasswordForm({
  labels,
  minLength = CHANGE_PASSWORD_MIN_LENGTH,
  onSubmit,
  onCancel,
  onDone,
}: ChangePasswordFormProps): JSX.Element {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    if (next.length < minLength) {
      setError(labels.errTooShort);
      return;
    }
    if (next !== confirm) {
      setError(labels.errMismatch);
      return;
    }
    if (next === current) {
      setError(labels.errSameAsCurrent);
      return;
    }
    if (!/^\d{6}$/.test(totpCode)) {
      setError(labels.errInvalidTotp);
      return;
    }
    setSubmitting(true);
    const res = await onSubmit(current, next, totpCode);
    setSubmitting(false);
    if (!res.ok) {
      if (res.error === 'invalid_credentials') setError(labels.errInvalidCredentials);
      else if (res.error === 'same_password') setError(labels.errSameAsCurrent);
      else if (res.error === 'not_authenticated') setError(labels.errNotAuthenticated);
      // Fallback: quem não passou o rótulo específico ainda recebe UMA mensagem de
      // erro — degradar para o genérico é pior que a mensagem certa, e infinitamente
      // melhor que quebrar a compilação de quem nunca pediu esta funcionalidade.
      else if (res.error === 'recarregar_pagina')
        setError(labels.errRecarregarPagina ?? labels.errGeneric);
      else setError(labels.errGeneric);
      return;
    }
    setDone(true);
  };

  if (done) {
    return (
      <Card padding="lg" data-testid="change-password-done">
        <h2 className="cf-card__title" style={{ marginBottom: 'var(--space-4, 16px)' }}>
          {labels.doneTitle}
        </h2>
        <Alert variant="success" role="status">
          {labels.doneMessage}
        </Alert>
        <div style={{ marginTop: 'var(--space-4, 16px)' }}>
          <Button block onClick={onDone}>
            {labels.doneButton}
          </Button>
        </div>
      </Card>
    );
  }

  return (
    <Card padding="lg" data-testid="change-password">
      <h2 className="cf-card__title" style={{ marginBottom: 'var(--space-2, 8px)' }}>
        {labels.title}
      </h2>
      <p style={{ color: 'var(--text-secondary)', marginBottom: 'var(--space-4, 16px)' }}>
        {labels.intro}
      </p>

      {error && (
        <div style={{ marginBottom: 'var(--space-4, 16px)' }}>
          <Alert variant="error" data-testid="change-password-error">
            {error}
          </Alert>
        </div>
      )}

      <form onSubmit={(e) => void handleSubmit(e)} aria-label={labels.title}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4, 16px)' }}>
          <Field label={labels.currentLabel}>
            {({ id }) => (
              <Input
                id={id}
                type="password"
                autoComplete="current-password"
                value={current}
                onChange={(e) => setCurrent(e.target.value)}
                required
              />
            )}
          </Field>
          <Field label={labels.newLabel}>
            {({ id }) => (
              <Input
                id={id}
                type="password"
                autoComplete="new-password"
                value={next}
                onChange={(e) => setNext(e.target.value)}
                required
              />
            )}
          </Field>
          <Field label={labels.confirmLabel}>
            {({ id }) => (
              <Input
                id={id}
                type="password"
                autoComplete="new-password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                required
              />
            )}
          </Field>
          <Field label={labels.totpLabel}>
            {({ id }) => (
              <Input
                id={id}
                type="text"
                inputMode="numeric"
                data-testid="cp-totp"
                value={totpCode}
                onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                required
              />
            )}
          </Field>
        </div>

        <div
          style={{ display: 'flex', gap: 'var(--space-2, 8px)', marginTop: 'var(--space-4, 16px)' }}
        >
          <Button type="submit" loading={submitting} data-testid="cp-submit">
            {submitting ? labels.submitting : labels.submit}
          </Button>
          <Button type="button" variant="ghost" onClick={onCancel} disabled={submitting}>
            {labels.cancel}
          </Button>
        </div>
      </form>
    </Card>
  );
}
